import { useReducedMotion } from "@reactuses/core";
import { X } from "lucide-react";
import { animate, motion, useMotionValue } from "motion/react";
import { useEffect, useEffectEvent, useRef } from "react";
import { createPortal } from "react-dom";

import { useKeyPress } from "../../../../hooks/useKeyPress";
import { cn } from "../../../../lib/cn";
import { useChat } from "../../ChatContext";
import {
    getTargetRect,
    type LightboxRect,
    readBottomSafeArea,
} from "./ImageLightboxLayout";

const EDGE_PADDING_PX = 24;
const OPENING_GESTURE_GUARD_MS = 450;
const ATTACHMENT_RADIUS_PX = 16;
const BACKDROP_EASE = [0.23, 1, 0.32, 1] as const;
const MORPH_EASE = [0.32, 0.72, 0, 1] as const;

type ImageLightboxProps = {
    src: string;
    alt: string;
    originRect: LightboxRect;
    naturalWidth: number;
    naturalHeight: number;
    onClose: () => void;
};

export function ImageLightbox({
    src,
    alt,
    originRect,
    naturalWidth,
    naturalHeight,
    onClose,
}: ImageLightboxProps) {
    const { labels, onImageLightboxChange } = useChat();
    const isReducedMotion = useReducedMotion();
    const notifyLightboxChange = useEffectEvent((open: boolean) => {
        onImageLightboxChange?.(open);
    });
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const isClosingRef = useRef(false);
    const canCloseRef = useRef(false);

    const targetRect = getTargetRect({
        naturalWidth,
        naturalHeight,
        padding: EDGE_PADDING_PX,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        bottomInset: readBottomSafeArea(),
    });
    const coverLayout = getObjectCoverLayout(
        naturalWidth,
        naturalHeight,
        originRect.width,
        originRect.height,
    );

    const frameTop = useMotionValue(isReducedMotion ? targetRect.top : originRect.top);
    const frameLeft = useMotionValue(
        isReducedMotion ? targetRect.left : originRect.left,
    );
    const frameWidth = useMotionValue(
        isReducedMotion ? targetRect.width : originRect.width,
    );
    const frameHeight = useMotionValue(
        isReducedMotion ? targetRect.height : originRect.height,
    );
    const frameRadius = useMotionValue(isReducedMotion ? 0 : ATTACHMENT_RADIUS_PX);

    const imageTop = useMotionValue(isReducedMotion ? 0 : coverLayout.top);
    const imageLeft = useMotionValue(isReducedMotion ? 0 : coverLayout.left);
    const imageWidth = useMotionValue(
        isReducedMotion ? targetRect.width : coverLayout.width,
    );
    const imageHeight = useMotionValue(
        isReducedMotion ? targetRect.height : coverLayout.height,
    );

    const backdropOpacity = useMotionValue(0);

    useEffect(() => {
        previouslyFocusedRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeButtonRef.current?.focus();

        if (isReducedMotion) {
            void animate(backdropOpacity, 1, { duration: 0.15 });
        } else {
            void animate(backdropOpacity, 1, {
                duration: 0.2,
                ease: BACKDROP_EASE,
            });
            void animate(frameTop, targetRect.top, {
                duration: 0.28,
                ease: MORPH_EASE,
            });
            void animate(frameLeft, targetRect.left, {
                duration: 0.28,
                ease: MORPH_EASE,
            });
            void animate(frameWidth, targetRect.width, {
                duration: 0.28,
                ease: MORPH_EASE,
            });
            void animate(frameHeight, targetRect.height, {
                duration: 0.28,
                ease: MORPH_EASE,
            });
            void animate(frameRadius, 0, { duration: 0.28, ease: MORPH_EASE });
            void animate(imageTop, 0, { duration: 0.28, ease: MORPH_EASE });
            void animate(imageLeft, 0, { duration: 0.28, ease: MORPH_EASE });
            void animate(imageWidth, targetRect.width, {
                duration: 0.28,
                ease: MORPH_EASE,
            });
            void animate(imageHeight, targetRect.height, {
                duration: 0.28,
                ease: MORPH_EASE,
            });
        }

        function swallowOpeningGestureClick(event: MouseEvent) {
            event.preventDefault();
            event.stopPropagation();
            document.removeEventListener("click", swallowOpeningGestureClick, true);
        }

        document.addEventListener("click", swallowOpeningGestureClick, true);

        const guardTimeoutId = window.setTimeout(() => {
            canCloseRef.current = true;
            document.removeEventListener("click", swallowOpeningGestureClick, true);
        }, OPENING_GESTURE_GUARD_MS);

        return () => {
            document.body.style.overflow = previousOverflow;
            canCloseRef.current = false;
            window.clearTimeout(guardTimeoutId);
            document.removeEventListener("click", swallowOpeningGestureClick, true);
        };
    }, [
        backdropOpacity,
        frameHeight,
        frameLeft,
        frameRadius,
        frameTop,
        frameWidth,
        imageHeight,
        imageLeft,
        imageTop,
        imageWidth,
        isReducedMotion,
        targetRect.height,
        targetRect.left,
        targetRect.top,
        targetRect.width,
    ]);

    useEffect(() => {
        notifyLightboxChange(true);

        return () => {
            notifyLightboxChange(false);
        };
    }, []);

    async function handleClose() {
        if (!canCloseRef.current || isClosingRef.current) {
            return;
        }

        isClosingRef.current = true;
        notifyLightboxChange(false);

        if (isReducedMotion) {
            await animate(backdropOpacity, 0, { duration: 0.12 });
        } else {
            await Promise.all([
                animate(backdropOpacity, 0, {
                    duration: 0.18,
                    ease: BACKDROP_EASE,
                }),
                animate(frameTop, originRect.top, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(frameLeft, originRect.left, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(frameWidth, originRect.width, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(frameHeight, originRect.height, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(frameRadius, ATTACHMENT_RADIUS_PX, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(imageTop, coverLayout.top, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(imageLeft, coverLayout.left, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(imageWidth, coverLayout.width, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
                animate(imageHeight, coverLayout.height, {
                    duration: 0.24,
                    ease: MORPH_EASE,
                }),
            ]);
        }

        previouslyFocusedRef.current?.focus();
        onClose();
    }

    useKeyPress("Escape", handleClose);

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            className="fixed inset-0 z-[500]"
        >
            <motion.button
                type="button"
                aria-label={labels.closeImage}
                className="absolute inset-0 bg-background-chat/70 backdrop-blur-md"
                style={{ opacity: backdropOpacity }}
                onClick={handleClose}
            />

            <motion.div
                className="pointer-events-none absolute overflow-hidden"
                style={{
                    top: frameTop,
                    left: frameLeft,
                    width: frameWidth,
                    height: frameHeight,
                    borderRadius: frameRadius,
                }}
            >
                <motion.img
                    src={src}
                    alt={alt}
                    draggable={false}
                    className="absolute max-w-none"
                    style={{
                        top: imageTop,
                        left: imageLeft,
                        width: imageWidth,
                        height: imageHeight,
                    }}
                />
            </motion.div>

            <button
                ref={closeButtonRef}
                type="button"
                aria-label={labels.closeImage}
                className={cn(
                    "absolute top-0 right-0 z-10 grid size-48 place-items-center",
                    "mt-[max(16px,env(safe-area-inset-top))] mr-[max(16px,env(safe-area-inset-right))]",
                    "cursor-pointer appearance-none rounded-full border-0 bg-surface-default/80 text-text-primary backdrop-blur-sm",
                    "transition-transform duration-150 ease-out active:scale-[0.92]",
                    "[-webkit-tap-highlight-color:transparent]",
                )}
                onClick={handleClose}
            >
                <X aria-hidden className="size-24" />
            </button>
        </div>,
        document.body,
    );
}

function getObjectCoverLayout(
    naturalWidth: number,
    naturalHeight: number,
    boxWidth: number,
    boxHeight: number,
): LightboxRect {
    const widthRatio = boxWidth / naturalWidth;
    const heightRatio = boxHeight / naturalHeight;
    const scale = Math.max(widthRatio, heightRatio);
    const width = naturalWidth * scale;
    const height = naturalHeight * scale;

    return {
        width,
        height,
        left: (boxWidth - width) / 2,
        top: (boxHeight - height) / 2,
    };
}
